import React, { useMemo } from "react";
import { SimpleTable } from "../../../components/ui";
import { getPayoutTimesheetColumns } from "./payoutTimesheetColumns";
import { PAYOUT_TIMESHEET_TABLE } from "./payoutTimesheetTableConfig";

/**
 * Renders a compact payout timesheet table via SimpleTable.
 *
 * @param {Object} props - Component props.
 * @param {Array<Object>} props.timesheets - Timesheet rows.
 * @param {Function} props.onSelect - Row click handler.
 * @param {boolean} props.quickBooksExported - Whether QuickBooks was exported for the week.
 * @param {string} [props.emptyMessage] - Empty table message override.
 * @returns {JSX.Element} Compact payout table.
 */
const PayoutTimesheetCompactTable = ({
  timesheets = [],
  onSelect,
  quickBooksExported = false,
  emptyMessage = PAYOUT_TIMESHEET_TABLE.emptyMessage,
}) => {
  const columns = useMemo(
    () => getPayoutTimesheetColumns({ quickBooksExported }),
    [quickBooksExported]
  );

  /**
   * Builds row interaction/style props for actionable vs muted timesheets.
   *
   * @param {Object} timesheet - Dashboard timesheet row.
   * @returns {Object} Props spread onto the table row.
   */
  const getRowProps = (timesheet) => {
    const isActionable = timesheet.isActionable !== false;

    return {
      className: isActionable
        ? PAYOUT_TIMESHEET_TABLE.rowClassName.actionable
        : PAYOUT_TIMESHEET_TABLE.rowClassName.muted,
      "aria-disabled": !isActionable,
      onClick: () => {
        if (isActionable) {
          onSelect(timesheet.worker, timesheet.weekEnding);
        }
      },
    };
  };

  return (
    <SimpleTable
      columns={columns}
      data={timesheets}
      getRowKey={(timesheet) => timesheet.id}
      getRowProps={getRowProps}
      includeSearch={PAYOUT_TIMESHEET_TABLE.includeSearch}
      emptyMessage={emptyMessage}
      className={PAYOUT_TIMESHEET_TABLE.className}
      tableClassName={PAYOUT_TIMESHEET_TABLE.tableClassName}
      theadClassName={PAYOUT_TIMESHEET_TABLE.theadClassName}
      headerRowClassName={PAYOUT_TIMESHEET_TABLE.headerRowClassName}
      tbodyClassName={PAYOUT_TIMESHEET_TABLE.tbodyClassName}
      cellClassName={PAYOUT_TIMESHEET_TABLE.cellClassName}
      rowClassName=""
    />
  );
};

export default PayoutTimesheetCompactTable;
